/**
 * 병원(appStore) 식별자 해석.
 *
 * 이 코드베이스는 **`-1` 을 "선택된 병원 없음"** 을 뜻하는 정상값으로 쓰고,
 * 분기가 전부 `appStoreId > -1` · `appStoreId !== -1` 로 적혀 있다.
 *
 * ⚠️ `NaN` 은 이 비교를 **조용히 통과한다** — `NaN > -1` 은 `false`, `NaN !== -1` 은 `true`.
 * 그래서 NaN 이 흘러 들어가면 잘못된 요청(`GET /user/apis/unrestricted/store/NaN`)만 나가는 게
 * 아니라 화면 분기까지 틀린다(`notFound()` 대신 가입 유도 화면이 뜬다).
 * 그 사고를 막으려고 파싱 실패를 `NaN` 이 아니라 `null` 로 돌려준다.
 */

/** "선택된 병원 없음". 라우트·스토어·뷰가 전부 이 값으로 분기하는 정상값이다. */
export const NO_APP_STORE_ID = -1;

/**
 * 우리가 실제로 만드는 URL 세그먼트의 모양.
 * 선행 0 없는 십진 양의 정수만 허용한다 — `/stores/007` 같은 주소는 우리가 만들지 않는다.
 * 공백도 허용하지 않는다(URL 이면 `%20` 으로 오지 그냥 공백으로 오지 않는다).
 */
const APP_STORE_ID_SEGMENT = /^(?:0|[1-9]\d*)$/;

/**
 * 실제 병원 식별자인가.
 * `NaN`·`-1`(미선택)·`0`·음수·소수·`Number.MAX_SAFE_INTEGER` 초과는 전부 `false`.
 */
export function isAppStoreId(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

/**
 * 라우트 파라미터를 병원 식별자로 해석한다.
 *
 * - `undefined` / 빈 배열 = **optional catch-all(`[[...appStoreId]]`) 무인자 접근**.
 *   `/dashboard` 를 그냥 여는 정상 동선이므로 오류가 아니라 `NO_APP_STORE_ID` 다.
 * - 숫자로 읽을 수 없으면 `null`. 호출부는 이걸 "요청하지 않고 404" 로 처리한다.
 *
 * `Number()` 를 그대로 쓰지 않는 이유: `Number('')`→0, `Number(' 5 ')`→5,
 * `Number('0x10')`→16, `Number('1e3')`→1000 처럼 URL 세그먼트로는 받으면 안 되는 값이
 * 조용히 통과한다. 그래서 정규식으로 모양부터 고정한다.
 */
export function parseAppStoreIdParam(raw: string | string[] | undefined | null): number | null {
  if (raw === undefined || raw === null) return NO_APP_STORE_ID;

  if (Array.isArray(raw)) {
    if (raw.length === 0) return NO_APP_STORE_ID;
    // catch-all 에 세그먼트가 두 개 이상 붙은 건 우리가 만드는 주소가 아니다.
    if (raw.length > 1) return null;
    return parseAppStoreIdParam(raw[0]);
  }

  // trim 하지 않는다 — 공백이 섞인 세그먼트는 우리가 만든 주소가 아니다.
  if (raw === String(NO_APP_STORE_ID)) return NO_APP_STORE_ID;
  if (!APP_STORE_ID_SEGMENT.test(raw)) return null;

  const parsed = Number(raw);
  return isAppStoreId(parsed) ? parsed : null;
}
